import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  Capacidades,
  EstadoComercial,
  Modulo,
  Permiso,
  SesionIniciada,
  UsuarioPublico,
} from '@exp/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { http, registrarAlVencerLaSesion, registrarProveedorDeToken } from '../api/http';

const CLAVE_TOKEN = 'expcolpo.token';

interface Consultorio {
  id: string;
  slug: string;
  nombre: string;
}

interface ValorSesion {
  usuario: UsuarioPublico | null;
  consultorio: Consultorio | null;
  /** Todavia se esta comprobando si hay una sesion guardada. */
  comprobando: boolean;
  /**
   * Si la sesion tiene un permiso, segun las capacidades que entrega el
   * servidor (`GET /auth/capacidades`, ADR-004).
   *
   * Antes se calculaba aqui con la matriz de roles. Ahora lo decide la misma
   * politica que autoriza en el backend, que ademas sabe lo que la interfaz no:
   * la contrasena provisional y, desde F1.5, los modulos contratados.
   *
   * Esto NO es seguridad: la autorizacion de verdad es la del servidor. Aqui
   * solo se decide que se ensena, para no ofrecer un boton que va a dar 403.
   */
  puede(permiso: Permiso): boolean;
  /**
   * Si el consultorio tiene el modulo contratado (F2.9). Para decidir que se
   * ofrece crear: leer lo ya creado no depende del modulo (AC-10).
   */
  tieneModulo(modulo: Modulo): boolean;
  /** Estado comercial efectivo del consultorio (F5.1); `null` mientras se comprueba. */
  estadoComercial: EstadoComercial | null;
  entrar(sesion: SesionIniciada): void;
  /**
   * Vuelve a leer la sesion del servidor sin perder el token.
   *
   * Hace falta cuando algo que viaja en la sesion cambia por una accion del
   * propio usuario —cambiar la contrasena apaga `debeCambiarContrasena`, y sin
   * releer se quedaria atrapado en la pantalla que le exige cambiarla.
   */
  refrescar(): Promise<void>;
  salir(): void;
  /**
   * El servidor dejo de reconocer la sesion a mitad del trabajo (401). El
   * acceso lo dice en lugar de aparecer sin explicacion. Se apaga al entrar.
   */
  sesionVencida: boolean;
}

const Contexto = createContext<ValorSesion | null>(null);

export function useSesion(): ValorSesion {
  const valor = useContext(Contexto);
  if (!valor) throw new Error('useSesion se usa dentro de ProveedorDeSesion');
  return valor;
}

function leerToken(): string | null {
  try {
    return localStorage.getItem(CLAVE_TOKEN);
  } catch {
    // Navegacion privada o almacenamiento bloqueado: la sesion vive solo en
    // memoria y se pierde al recargar. Es aceptable; fallar no lo es.
    return null;
  }
}

function guardarToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(CLAVE_TOKEN, token);
    else localStorage.removeItem(CLAVE_TOKEN);
  } catch {
    /* sin almacenamiento disponible */
  }
}

export function ProveedorDeSesion({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => leerToken());
  const [usuario, setUsuario] = useState<UsuarioPublico | null>(null);
  const [consultorio, setConsultorio] = useState<Consultorio | null>(null);
  const [comprobando, setComprobando] = useState<boolean>(() => leerToken() !== null);
  const [sesionVencida, setSesionVencida] = useState(false);
  const [capacidades, setCapacidades] = useState<Capacidades | null>(null);
  const clienteConsultas = useQueryClient();

  // El cliente HTTP pregunta por el token en cada peticion, en lugar de
  // recibirlo al construirse: asi no queda un token viejo pegado.
  useEffect(() => {
    registrarProveedorDeToken(() => token);
  }, [token]);

  /** Olvida la sesion en este navegador. No habla con el servidor. */
  const olvidar = useCallback(() => {
    guardarToken(null);
    setToken(null);
    setUsuario(null);
    setConsultorio(null);
    setCapacidades(null);
    // Nada de un expediente se queda en memoria al salir: la siguiente
    // persona en este equipo no debe verlo ni un instante.
    clienteConsultas.clear();
  }, [clienteConsultas]);

  /**
   * Salir de verdad: el servidor revoca la sesion (FIND-023) y luego se olvida
   * aqui. Si el servidor no responde, se olvida igual: la persona pidio salir.
   */
  const salir = useCallback(() => {
    if (token) void http.post('/auth/salir').catch(() => undefined);
    olvidar();
  }, [token, olvidar]);

  useEffect(() => {
    // Un 401 significa que el servidor ya no reconoce la sesion: no hay nada
    // que revocar, solo olvidarla. Llamar a /auth/salir aqui daria otro 401.
    registrarAlVencerLaSesion(() => {
      setSesionVencida(true);
      olvidar();
    });
  }, [olvidar]);

  const entrar = useCallback((sesion: SesionIniciada) => {
    setSesionVencida(false);
    setCapacidades(null);
    guardarToken(sesion.token);
    setToken(sesion.token);
    setUsuario(sesion.usuario);
    setConsultorio(sesion.organizacion);
  }, []);

  // Las capacidades se piden cada vez que cambia quien esta dentro. Mientras
  // llegan, la sesion cuenta como «comprobando» y no se ofrece nada.
  useEffect(() => {
    if (!token || !usuario || capacidades) return;
    let vigente = true;
    http
      .get('/auth/capacidades')
      .then(({ data }) => {
        if (vigente) setCapacidades(data.datos as Capacidades);
      })
      .catch(() => {
        if (vigente) olvidar();
      });
    return () => {
      vigente = false;
    };
  }, [token, usuario, capacidades, olvidar]);

  // Al recargar la pagina se rehidrata la sesion contra el servidor. No se
  // confia en lo que hubiera guardado el navegador: el rol o la cuenta pueden
  // haber cambiado desde que se emitio el token.
  useEffect(() => {
    if (!token || usuario) return;

    let vigente = true;
    registrarProveedorDeToken(() => token);

    http
      .get('/auth/yo')
      .then(({ data }) => {
        if (!vigente) return;
        setUsuario(data.datos.usuario);
        setConsultorio(data.datos.organizacion);
      })
      .catch(() => {
        // El token guardado ya no vale: no hay sesion que revocar.
        if (vigente) olvidar();
      })
      .finally(() => {
        if (vigente) setComprobando(false);
      });

    return () => {
      vigente = false;
    };
  }, [token, usuario, olvidar]);

  const refrescar = useCallback(async () => {
    if (!token) return;
    const { data } = await http.get('/auth/yo');
    setUsuario(data.datos.usuario);
    setConsultorio(data.datos.organizacion);
    // Lo que cambio (p. ej. ya no hay contrasena provisional) cambia tambien
    // lo que se puede hacer: se vuelven a pedir.
    setCapacidades(null);
  }, [token]);

  const permisos = useMemo(() => new Set(capacidades?.permisos ?? []), [capacidades]);
  const puede = useCallback((permiso: Permiso) => permisos.has(permiso), [permisos]);
  const modulos = useMemo(
    () => new Set((capacidades?.modulos ?? []).map((m) => m.modulo)),
    [capacidades],
  );
  const tieneModulo = useCallback((modulo: Modulo) => modulos.has(modulo), [modulos]);

  // Hay sesion pero aun no llegan sus capacidades: todavia se esta comprobando,
  // para que ninguna pantalla decida con un «no puede» que solo es «no se sabe».
  const comprobandoTodo = comprobando || (usuario !== null && capacidades === null);

  const valor = useMemo<ValorSesion>(
    () => ({
      usuario,
      consultorio,
      comprobando: comprobandoTodo,
      puede,
      tieneModulo,
      estadoComercial: capacidades?.estadoComercial ?? null,
      entrar,
      refrescar,
      salir,
      sesionVencida,
    }),
    [
      usuario,
      consultorio,
      comprobandoTodo,
      puede,
      tieneModulo,
      capacidades,
      entrar,
      refrescar,
      salir,
      sesionVencida,
    ],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}
